import { API_BASE_URL, joinApiUrl } from '@/services/api.config';
import { getToken } from '@/services/auth.session';
import type { AppEventsBatch } from '@/types/analytics';

// API_BASE_URL already ends in `/compass/api/v1` (both proxy and legacy modes — see
// api.config.ts), so this path must NOT repeat `/api/v1`.
const EVENTS_PATH = '/analytics/events';

// POST a batch and report whether the backend accepted it. Loss-tolerant: never throws — any
// failure (network error, non-OK response, missing token) resolves `false` so the caller
// (index.ts's flush()) knows to leave the events in durable storage for the next attempt.
// A single batch can carry events for multiple orgs (org_id lives per-event now, validated
// server-side).
export async function sendBatch(batch: AppEventsBatch): Promise<boolean> {
  try {
    const token = getToken();
    if (!token) return false; // unauthenticated — caller retries later
    const url = joinApiUrl(API_BASE_URL, EVENTS_PATH);
    const res = await fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(batch),
    });
    return res.ok;
  } catch {
    /* never throw from analytics */
    return false;
  }
}

// Best-effort send used from pagehide/visibilitychange, where we cannot await a response
// before the page unloads. Fire-and-forget: the promise result is intentionally ignored — a
// successful send is a bonus, a failed one is retried on next load since storage isn't cleared
// from this path.
export function sendBatchBeacon(batch: AppEventsBatch): void {
  void sendBatch(batch);
}
